import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  AutoComplete,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  message,
} from "antd";
import dayjs from "dayjs";
import Icon from "../../../../../components/AppIcon";
import { uploadSingleFile } from "../../../../../api/uploads";
import { loansApi } from "../../../../../api/loans";
import { IRDAI_INSURANCE_COMPANIES } from "../../../../../constants/irdaiInsuranceCompanies";
import useShowroomAutoSuggest from "../../../../../hooks/useShowroomAutoSuggest";
import { useVehicleRegistrationLookup } from "../../../../../hooks/useVehicleRegistrationLookup";
import { buildVehicleRecordAutofillPatch } from "../../../../../utils/vehicleRecordAutofill";
import PostFileDocumentManagement from "../post-file/PostFileDocumentManagement";
import LoanDocumentViewerModal from "../../shared/LoanDocumentViewerModal";
import LoanDocumentUploadModal from "../../shared/LoanDocumentUploadModal";

const inputClassName = "h-10";
const textAreaClassName = "min-h-[84px]";
const AUTO_LOAN_NUMBER_REGEX = /^LN-\d{4}-\d+$/i;

const isAutoGeneratedLoanNumber = (value) =>
  AUTO_LOAN_NUMBER_REGEX.test(String(value || "").trim());

const toPersistablePostFileDocuments = (docs = []) =>
  (Array.isArray(docs) ? docs : [])
    .filter((doc) => doc && typeof doc === "object")
    .filter(
      (doc) =>
        typeof doc?.url !== "string" || !String(doc.url).startsWith("blob:"),
    )
    .map((doc) => ({
      id: doc?.id,
      name: doc?.name || "",
      size: doc?.size || "",
      uploadedBy: doc?.uploadedBy || "",
      uploadedAt: doc?.uploadedAt || "",
      status: doc?.status || "",
      tagId: doc?.tagId || null,
      tag: doc?.tag || null,
      url: doc?.url || "",
      format: doc?.format || "",
      publicId: doc?.publicId || null,
      isPreFile: Boolean(doc?.isPreFile),
      source: doc?.source || "",
      deliveryDocType: doc?.deliveryDocType || "",
    }));

// Helper component to handle date input formatting
// Converts incoming value (DayJS, Date, timestamp, string) to yyyy-MM-dd
// Returns yyyy-MM-dd string on change
const DateInput = ({ value, onChange, ...props }) => {
  const parsedValue = React.useMemo(() => {
    if (!value) return null;
    try {
      // If it's a DayJS object (has format function)
      if (value && typeof value.format === "function") {
        return value;
      }
      // If it's a string or number (timestamp)
      const parsed = dayjs(value);
      if (parsed.isValid()) {
        return parsed;
      }
    } catch (e) {
      console.warn("Date parsing error", e);
      return null;
    }
    return null;
  }, [value]);

  return (
    <DatePicker
      className="h-10 w-full"
      format="DD-MM-YYYY"
      value={parsedValue || null}
      onChange={(date) => onChange?.(date ? date.format("YYYY-MM-DD") : "")}
      allowClear
      {...props}
    />
  );
};

const POLICY_DURATION_OPTIONS = [
  { value: "1", label: "1 Yr OD + 3 Yrs TP" },
  { value: "2", label: "2 Yrs OD + 3 Yrs TP" },
  { value: "3", label: "3 Yrs OD + 3 Yrs TP" },
];

const addYearsToIsoDate = (dateString, yearsToAdd) => {
  if (!dateString || !yearsToAdd) return "";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  const nextDate = new Date(date);
  nextDate.setFullYear(nextDate.getFullYear() + Number(yearsToAdd));
  nextDate.setDate(nextDate.getDate() - 1);

  if (Number.isNaN(nextDate.getTime())) return "";
  return nextDate.toISOString().split("T")[0];
};

const DeliverySection = ({ id, icon, iconTone, title, aside, children }) => (
  <section
    id={id}
    className="rounded-[24px] border border-border/70 bg-card p-4 shadow-[0_18px_40px_-34px_rgba(15,23,42,0.16)] dark:bg-black/20 md:p-6"
  >
    <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex items-center gap-3">
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-xl ${iconTone}`}
        >
          <Icon name={icon} size={17} />
        </div>
        <div>
          <h2 className="text-base font-semibold tracking-tight text-foreground md:text-lg">
            {title}
          </h2>
        </div>
      </div>
      {aside}
    </div>
    {children}
  </section>
);

const UploadField = ({
  label,
  file,
  uploading,
  onOpenUpload,
  onView,
  onRemove,
}) => {
  const displayLabel =
    String(label || "")
      .replace(/^upload\s+/i, "")
      .trim() || "Document";
  return (
    <div>
    <label className="mb-1 block text-xs text-muted-foreground">{label}</label>
    {!file ? (
      <button
        type="button"
        onClick={onOpenUpload}
        className={`flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-dashed px-4 text-sm transition-all ${
          uploading
            ? "cursor-not-allowed border-slate-300/60 bg-slate-100/60 opacity-60 dark:border-slate-700 dark:bg-slate-900/50"
            : "cursor-pointer border-slate-300/80 bg-white hover:border-sky-300 hover:bg-sky-50 dark:border-slate-700 dark:bg-slate-950/70 dark:hover:bg-slate-900"
        }`}
        disabled={uploading}
      >
        {uploading ? (
          <>
            <Spin size="small" />
            <span className="text-muted-foreground">Uploading...</span>
          </>
        ) : (
          <>
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              <Icon name="Upload" size={14} />
            </span>
            <span className="font-medium text-slate-600 dark:text-slate-300">
              Upload File
            </span>
          </>
        )}
      </button>
    ) : (
      <div className="rounded-2xl border border-border/70 bg-muted/20 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex max-w-[180px] items-center gap-1.5 truncate rounded-xl bg-white px-3 py-2 text-sm font-medium text-foreground shadow-sm dark:bg-slate-950/70">
            <Icon
              name="FileText"
              size={14}
              className="text-sky-600 dark:text-sky-300"
            />
            {displayLabel}
          </span>
          <button
            type="button"
            onClick={onView}
            className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-1.5 text-sm font-medium text-sky-700 transition hover:bg-sky-100 dark:border-sky-900/60 dark:bg-sky-500/10 dark:text-sky-200"
          >
            View
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/70 dark:text-slate-200"
          >
            Remove
          </button>
        </div>
      </div>
    )}
  </div>
  );
};

/**
 * VEHICLE DELIVERY STEP COMPONENT
 *
 * PURPOSE:
 * Manages vehicle delivery details including invoice, RC, and insurance information
 *
 * BACKEND INTEGRATION:
 * - Data Source/Update: Loan model
 * - Fields Managed:
 *   • delivery_date: Date of vehicle delivery
 *   • delivery_dealerName, delivery_dealerContactPerson, delivery_dealerContactNumber, delivery_dealerAddress
 *   • delivery_by: Who handled delivery ("Dealer" / "Autocredits")
 *   • insurance_by: Insurance handled by (default: "Autocredits India LLP")
 *   • insurance_company_name, insurance_policy_number
 *   • invoice_number, invoice_date, invoice_received_as, invoice_received_from, invoice_received_date
 *   • rc_redg_no, rc_chassis_no, rc_engine_no, rc_redg_date
 *   • rc_received_as, rc_received_from, rc_received_date
 *   • delivery_invoiceFile, delivery_rcFile, delivery_rcBackFile: File uploads (storage URLs)
 */
const VehicleDeliveryStep = ({ form }) => {
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [rcFile, setRcFile] = useState(null);
  const [rcBackFile, setRcBackFile] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(false);
  const [viewRc, setViewRc] = useState(false);
  const [viewRcBack, setViewRcBack] = useState(false);
  const [showInvoiceUploadModal, setShowInvoiceUploadModal] = useState(false);
  const [showRcUploadModal, setShowRcUploadModal] = useState(false);
  const [showRcBackUploadModal, setShowRcBackUploadModal] = useState(false);

  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [uploadingRc, setUploadingRc] = useState(false);
  const [uploadingRcBack, setUploadingRcBack] = useState(false);
  const [invoiceUploadProgress, setInvoiceUploadProgress] = useState(0);
  const [rcUploadProgress, setRcUploadProgress] = useState(0);
  const [rcBackUploadProgress, setRcBackUploadProgress] = useState(0);
  const rcInvReservationInFlightRef = React.useRef(false);
  const {
    options: registrationLookupOptions,
    loading: registrationLookupLoading,
    search: searchRegistrationLookup,
    resolveSelectedRecord: resolveSelectedRegistrationRecord,
  } = useVehicleRegistrationLookup({ minChars: 2, limit: 20 });

  const loanType =
    Form.useWatch("typeOfLoan", form) || form.getFieldValue("typeOfLoan");
  const isFinanced =
    Form.useWatch("isFinanced", form) || form.getFieldValue("isFinanced");
  const approvalBankName =
    Form.useWatch("approval_bankName", form) ||
    form.getFieldValue("approval_bankName");
  const rcInvStorageNumber =
    Form.useWatch("rc_inv_storage_number", form) ||
    form.getFieldValue("rc_inv_storage_number");
  const insuranceBy =
    Form.useWatch("insurance_by", form) || form.getFieldValue("insurance_by");
  const vehicleMake = Form.useWatch("vehicleMake", form);
  const recordIdValue = Form.useWatch("_id", form);
  const loanIdValue = Form.useWatch("loanId", form);
  const loanNumber = Form.useWatch("loan_number", form);
  const createdAt = Form.useWatch("createdAt", form);
  const insuranceStartDate = Form.useWatch("insurance_policy_start_date", form);
  const insurancePolicyDuration = Form.useWatch(
    "insurance_policy_duration_od",
    form,
  );
  const currentPostFileDocuments = Form.useWatch("postfile_documents", form);

  const isNewCar = loanType === "New Car";
  const normalizedLoanType = String(loanType || "").trim().toLowerCase();
  const isCashCase =
    String(isFinanced || "").trim().toLowerCase() === "no" ||
    normalizedLoanType === "cash car" ||
    normalizedLoanType === "cash sale" ||
    normalizedLoanType === "cash";
  const showHypothecation = isFinanced === "Yes";
  const { options: showroomOptions, search: searchShowrooms } =
    useShowroomAutoSuggest({ limit: 25, brand: vehicleMake });
  const deliveryDealerNameInput = Form.useWatch("delivery_dealerName", form);
  const showroomDealerName = Form.useWatch("showroomDealerName", form);
  const showroomDealerAddress = Form.useWatch("showroomDealerAddress", form);
  const showroomDealerContactPerson = Form.useWatch(
    "showroomDealerContactPerson",
    form,
  );
  const showroomDealerContactNumber = Form.useWatch(
    "showroomDealerContactNumber",
    form,
  );
  const filteredShowroomOptions = useMemo(() => {
    const tokens = String(deliveryDealerNameInput || "")
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!tokens.length) return showroomOptions;
    return (showroomOptions || []).filter((option) => {
      const name = String(option?.showroom?.name || option?.value || "")
        .toLowerCase()
        .trim();
      return tokens.every((token) => name.includes(token));
    });
  }, [deliveryDealerNameInput, showroomOptions]);

  const effectiveLoanRecordId = useMemo(() => {
    const direct =
      recordIdValue ||
      loanIdValue ||
      (isAutoGeneratedLoanNumber(loanNumber) ? "" : loanNumber);
    if (direct) return String(direct);
    if (typeof window === "undefined") return "";
    const match = window.location.pathname.match(/\/loans\/edit\/([^/?#]+)/i);
    return match?.[1] ? String(match[1]) : "";
  }, [recordIdValue, loanIdValue, loanNumber]);

  const syncDealerFields = (patch = {}) => {
    const next = { ...patch };
    if (Object.prototype.hasOwnProperty.call(patch, "delivery_dealerName")) {
      next.showroomDealerName = patch.delivery_dealerName;
    }
    if (
      Object.prototype.hasOwnProperty.call(
        patch,
        "delivery_dealerContactPerson",
      )
    ) {
      next.showroomDealerContactPerson = patch.delivery_dealerContactPerson;
    }
    if (
      Object.prototype.hasOwnProperty.call(
        patch,
        "delivery_dealerContactNumber",
      )
    ) {
      next.showroomDealerContactNumber = patch.delivery_dealerContactNumber;
    }
    if (Object.prototype.hasOwnProperty.call(patch, "delivery_dealerAddress")) {
      next.showroomDealerAddress = patch.delivery_dealerAddress;
    }
    form.setFieldsValue(next);
  };

  const handleShowroomSelect = (_, option) => {
    const showroom = option?.showroom;
    if (!showroom) return;
    syncDealerFields({
      delivery_dealerName: showroom.name || "",
      delivery_dealerContactPerson: showroom.contact_person || "",
      delivery_dealerContactNumber: showroom.phone || "",
      delivery_dealerAddress: showroom.address || "",
    });
  };

  const handleRegistrationSelect = (_, option) => {
    const record = resolveSelectedRegistrationRecord(_, option);
    if (!record) return;
    const patch = buildVehicleRecordAutofillPatch(record, "rc_redg_no");
    if (Object.keys(patch).length) {
      form.setFieldsValue(patch);
    }
  };

  const buildDeliveryDocEntry = (file, uploadData, tag, deliveryDocType) => ({
    id:
      uploadData?.public_id ||
      `${deliveryDocType.toLowerCase()}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    name: file?.name || `${tag} Document`,
    size: formatFileSize(file?.size),
    uploadedBy: "Current User",
    uploadedAt: new Date().toLocaleString("en-IN"),
    status: uploadData ? "submitted" : "pending",
    tagId: null,
    tag,
    url: uploadData?.url || "",
    format: uploadData?.format || String(file?.type || "").split("/")[1] || "unknown",
    publicId: uploadData?.public_id || null,
    source: "Delivery Upload",
    deliveryDocType,
  });

  const mergeDeliveryDocument = (docs = [], entry, deliveryDocType) => {
    const list = Array.isArray(docs) ? [...docs] : [];
    const next = list.filter(
      (doc) =>
        !(doc?.deliveryDocType === deliveryDocType) &&
        !(String(doc?.tag || "").toLowerCase() === String(entry?.tag || "").toLowerCase() && doc?.source === "Delivery Upload"),
    );
    next.push(entry);
    return next;
  };

  const removeDeliveryDocument = (docs = [], deliveryDocType) =>
    (Array.isArray(docs) ? docs : []).filter(
      (doc) => doc?.deliveryDocType !== deliveryDocType,
    );

  const resolvePersistTargets = useCallback(() => {
    const fromPath =
      typeof window !== "undefined"
        ? window.location.pathname.match(/\/loans\/edit\/([^/?#]+)/i)?.[1] || ""
        : "";
    const candidates = [
      effectiveLoanRecordId,
      recordIdValue,
      loanIdValue,
      isAutoGeneratedLoanNumber(loanNumber) ? "" : loanNumber,
      fromPath,
    ]
      .map((v) => String(v || "").trim())
      .filter(Boolean);
    return Array.from(new Set(candidates));
  }, [effectiveLoanRecordId, recordIdValue, loanIdValue, loanNumber]);

  const persistDeliveryPatch = useCallback(async (patch = {}, { silent = true } = {}) => {
    const targets = resolvePersistTargets();
    if (!targets.length) {
      if (!silent) {
        message.error("Could not resolve loan id to save delivery document details");
      }
      return false;
    }

    let lastError = null;
    for (const id of targets) {
      try {
        await loansApi.update(id, patch);
        return true;
      } catch (error) {
        lastError = error;
      }
    }

    console.error("Failed to auto-persist delivery patch:", lastError);
    if (!silent) {
      message.error(lastError?.message || "Failed to save delivery document details");
    }
    return false;
  }, [resolvePersistTargets]);

  // Pre-fill dealer details from vehicle verification
  useEffect(() => {
    const patch = {};
    if (showroomDealerName && !form.getFieldValue("delivery_dealerName")) {
      patch.delivery_dealerName = showroomDealerName;
    }
    if (
      showroomDealerContactPerson &&
      !form.getFieldValue("delivery_dealerContactPerson")
    ) {
      patch.delivery_dealerContactPerson = showroomDealerContactPerson;
    }
    if (
      showroomDealerContactNumber &&
      !form.getFieldValue("delivery_dealerContactNumber")
    ) {
      patch.delivery_dealerContactNumber = showroomDealerContactNumber;
    }
    if (
      showroomDealerAddress &&
      !form.getFieldValue("delivery_dealerAddress")
    ) {
      patch.delivery_dealerAddress = showroomDealerAddress;
    }
    if (Object.keys(patch).length) {
      form.setFieldsValue(patch);
    }
  }, [
    form,
    showroomDealerName,
    showroomDealerContactPerson,
    showroomDealerContactNumber,
    showroomDealerAddress,
  ]);

  // Initialize static defaults only for genuinely new loans.
  useEffect(() => {
    const patch = {};

    if (!form.getFieldValue("insurance_by")) {
      patch.insurance_by = "Autocredits India LLP";
    }
    if (Object.keys(patch).length) {
      form.setFieldsValue(patch);
    }
  }, [form, loanIdValue, loanNumber, createdAt]);

  // Reserve RC/INV storage number via backend atomic counter.
  useEffect(() => {
    if (form.getFieldValue("rc_inv_storage_number")) return;
    if (rcInvReservationInFlightRef.current) return;
    if (!resolvePersistTargets().length) return;

    let cancelled = false;
    rcInvReservationInFlightRef.current = true;

    const reserveRcInvNumber = async () => {
      try {
        const response = await loansApi.getNextRcInvStorageNumber();
        const nextValue =
          response?.data?.rcInvStorageNumber ||
          response?.data?.nextNumber ||
          response?.data?.value ||
          response?.rcInvStorageNumber ||
          response?.nextNumber ||
          response?.value ||
          "";

        if (cancelled) return;
        if (!nextValue) throw new Error("Counter service returned empty value");
        if (!form.getFieldValue("rc_inv_storage_number")) {
          form.setFieldValue("rc_inv_storage_number", String(nextValue));
          void persistDeliveryPatch({
            rc_inv_storage_number: String(nextValue),
          }, { silent: false });
        }
      } catch (error) {
        if (!cancelled) {
          message.error(
            error?.message || "Failed to reserve RC/INV storage number",
          );
        }
      } finally {
        rcInvReservationInFlightRef.current = false;
      }
    };

    void reserveRcInvNumber();
    return () => {
      cancelled = true;
      rcInvReservationInFlightRef.current = false;
    };
  }, [form, effectiveLoanRecordId, persistDeliveryPatch, resolvePersistTargets]);

  // Heal legacy/inconsistent records: if delivery file fields are blank but tagged docs exist, backfill once.
  useEffect(() => {
    if (!effectiveLoanRecordId) return;
    const docs = form.getFieldValue("postfile_documents") || currentPostFileDocuments || [];
    if (!Array.isArray(docs) || !docs.length) return;

    const invoiceDoc = docs.find(
      (doc) =>
        doc &&
        (String(doc?.deliveryDocType || "").toUpperCase() === "INVOICE" ||
          String(doc?.tag || "").toLowerCase() === "invoice") &&
        String(doc?.url || "").startsWith("http"),
    );
    const rcDoc = docs.find(
      (doc) =>
        doc &&
        (String(doc?.deliveryDocType || "").toUpperCase() === "RC" ||
          String(doc?.tag || "").toLowerCase() === "rc") &&
        String(doc?.url || "").startsWith("http"),
    );
    const rcBackDoc = docs.find(
      (doc) =>
        doc &&
        (String(doc?.deliveryDocType || "").toUpperCase() === "RC_BACK" ||
          String(doc?.tag || "").toLowerCase() === "rc back") &&
        String(doc?.url || "").startsWith("http"),
    );

    const patch = {};
    if (!form.getFieldValue("delivery_invoiceFile") && invoiceDoc?.url) {
      patch.delivery_invoiceFile = invoiceDoc.url;
      form.setFieldValue("delivery_invoiceFile", invoiceDoc.url);
      setInvoiceFile((prev) => prev || { name: invoiceDoc.name || "Invoice", size: invoiceDoc.size || "", url: invoiceDoc.url });
    }
    if (!form.getFieldValue("delivery_rcFile") && rcDoc?.url) {
      patch.delivery_rcFile = rcDoc.url;
      form.setFieldValue("delivery_rcFile", rcDoc.url);
      setRcFile((prev) => prev || { name: rcDoc.name || "RC Front", size: rcDoc.size || "", url: rcDoc.url });
    }
    if (!form.getFieldValue("delivery_rcBackFile") && rcBackDoc?.url) {
      patch.delivery_rcBackFile = rcBackDoc.url;
      form.setFieldValue("delivery_rcBackFile", rcBackDoc.url);
      setRcBackFile((prev) => prev || { name: rcBackDoc.name || "RC Back", size: rcBackDoc.size || "", url: rcBackDoc.url });
    }
    if (Object.keys(patch).length) {
      void persistDeliveryPatch(patch);
    }
  }, [form, effectiveLoanRecordId, currentPostFileDocuments, persistDeliveryPatch]);

  // Sync local file state to the form (URL String only)
  useEffect(() => {
    if (invoiceFile !== undefined) {
      form.setFieldValue(
        "delivery_invoiceFile",
        invoiceFile ? invoiceFile.url : null,
      );
    }
    if (rcFile !== undefined) {
      form.setFieldValue("delivery_rcFile", rcFile ? rcFile.url : null);
    }
    if (rcBackFile !== undefined) {
      form.setFieldValue("delivery_rcBackFile", rcBackFile ? rcBackFile.url : null);
    }
  }, [invoiceFile, rcFile, rcBackFile, form]);

  // Load initial files from form if editing
  // We use a ref to track if we've initialized to avoid overwrite loops
  const initializedFiles = React.useRef(false);

  useEffect(() => {
    if (initializedFiles.current) return;

    const initialInvoice = form.getFieldValue("delivery_invoiceFile");
    const initialRc = form.getFieldValue("delivery_rcFile");
    const initialRcBack = form.getFieldValue("delivery_rcBackFile");

    // Helper to safely parse initial value
    const parseInitialFile = (val) => {
      if (!val) return null;
      if (typeof val === "string") {
        // It's a URL string from DB
        const name = val.split("/").pop() || "Document";
        return { name: name, size: "", url: val };
      }
      // It's an object (maybe from draft)
      return val;
    };

    if (initialInvoice) setInvoiceFile(parseInitialFile(initialInvoice));
    if (initialRc) setRcFile(parseInitialFile(initialRc));
    if (initialRcBack) setRcBackFile(parseInitialFile(initialRcBack));

    initializedFiles.current = true;
  }, [form]);

  useEffect(() => {
    const calculatedOdEndDate = addYearsToIsoDate(
      insuranceStartDate,
      insurancePolicyDuration,
    );

    if (
      form.getFieldValue("insurance_policy_end_date_od") !== calculatedOdEndDate
    ) {
      form.setFieldValue(
        "insurance_policy_end_date_od",
        calculatedOdEndDate || "",
      );
    }
  }, [form, insurancePolicyDuration, insuranceStartDate]);

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const handleInvoiceUpload = async (files = []) => {
    const file = Array.isArray(files) ? files[0] : files;
    if (!file) return;
    try {
      setUploadingInvoice(true);
      setInvoiceUploadProgress(10);
      const data = await uploadSingleFile(file);
      setInvoiceUploadProgress(75);

      const nextFile = {
        name: file.name,
        size: formatFileSize(file.size),
        url: data.url,
      };
      setInvoiceFile(nextFile);

      const currentDocs = form.getFieldValue("postfile_documents") || currentPostFileDocuments || [];
      const invoiceDoc = buildDeliveryDocEntry(file, data, "Invoice", "INVOICE");
      const mergedDocs = mergeDeliveryDocument(currentDocs, invoiceDoc, "INVOICE");
      form.setFieldValue("postfile_documents", mergedDocs);

      await persistDeliveryPatch({
        delivery_invoiceFile: data.url || "",
        postfile_documents: toPersistablePostFileDocuments(mergedDocs),
      }, { silent: false });
      setInvoiceUploadProgress(100);
      message.success("Invoice uploaded successfully");
    } catch (error) {
      console.error("Upload failed", error);
      message.error("Failed to upload invoice. Please try again.");
    } finally {
      setUploadingInvoice(false);
      setTimeout(() => setInvoiceUploadProgress(0), 180);
    }
  };

  const handleRcUpload = async (files = []) => {
    const file = Array.isArray(files) ? files[0] : files;
    if (!file) return;
    try {
      setUploadingRc(true);
      setRcUploadProgress(10);
      const data = await uploadSingleFile(file);
      setRcUploadProgress(75);

      const nextFile = {
        name: file.name,
        size: formatFileSize(file.size),
        url: data.url,
      };
      setRcFile(nextFile);

      const currentDocs = form.getFieldValue("postfile_documents") || currentPostFileDocuments || [];
      const rcDoc = buildDeliveryDocEntry(file, data, "RC", "RC");
      const mergedDocs = mergeDeliveryDocument(currentDocs, rcDoc, "RC");
      form.setFieldValue("postfile_documents", mergedDocs);

      await persistDeliveryPatch({
        delivery_rcFile: data.url || "",
        postfile_documents: toPersistablePostFileDocuments(mergedDocs),
      }, { silent: false });
      setRcUploadProgress(100);
      message.success("RC uploaded successfully");
    } catch (error) {
      console.error("Upload failed", error);
      message.error("Failed to upload RC. Please try again.");
    } finally {
      setUploadingRc(false);
      setTimeout(() => setRcUploadProgress(0), 180);
    }
  };

  const handleRcBackUpload = async (files = []) => {
    const file = Array.isArray(files) ? files[0] : files;
    if (!file) return;
    try {
      setUploadingRcBack(true);
      setRcBackUploadProgress(10);
      const data = await uploadSingleFile(file);
      setRcBackUploadProgress(75);

      const nextFile = {
        name: file.name,
        size: formatFileSize(file.size),
        url: data.url,
      };
      setRcBackFile(nextFile);

      const currentDocs = form.getFieldValue("postfile_documents") || currentPostFileDocuments || [];
      const rcBackDoc = buildDeliveryDocEntry(file, data, "RC Back", "RC_BACK");
      const mergedDocs = mergeDeliveryDocument(currentDocs, rcBackDoc, "RC_BACK");
      form.setFieldValue("postfile_documents", mergedDocs);

      await persistDeliveryPatch({
        delivery_rcBackFile: data.url || "",
        postfile_documents: toPersistablePostFileDocuments(mergedDocs),
      }, { silent: false });
      setRcBackUploadProgress(100);
      message.success("RC back uploaded successfully");
    } catch (error) {
      console.error("Upload failed", error);
      message.error("Failed to upload RC back. Please try again.");
    } finally {
      setUploadingRcBack(false);
      setTimeout(() => setRcBackUploadProgress(0), 180);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Delivery Details Section */}
      {isNewCar && (
        <DeliverySection
          id="delivery-details"
          icon="Truck"
          iconTone="bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-200"
          title="Delivery Details"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
              <Form.Item
                label="Delivery Date"
                name="delivery_date"
                className="mb-0"
                getValueProps={(val) => ({ value: val })}
              >
                <DateInput />
              </Form.Item>

              <Form.Item
                label="Dealer Name"
                name="delivery_dealerName"
                className="mb-0"
              >
                <AutoComplete
                  options={filteredShowroomOptions}
                  popupMatchSelectWidth={false}
                  onSearch={searchShowrooms}
                  onSelect={handleShowroomSelect}
                  onChange={(value) => {
                    searchShowrooms(value);
                    syncDealerFields({ delivery_dealerName: value || "" });
                  }}
                  filterOption={(inputValue, option) =>
                    String(option?.showroom?.name || option?.value || "")
                      .toLowerCase()
                      .includes(String(inputValue || "").toLowerCase().trim())
                  }
                >
                  <Input className={inputClassName} />
                </AutoComplete>
              </Form.Item>

              <Form.Item
                label="Contact Person"
                name="delivery_dealerContactPerson"
                className="mb-0"
              >
                <Input
                  className={inputClassName}
                  onChange={(e) =>
                    syncDealerFields({
                      delivery_dealerContactPerson: e.target.value || "",
                    })
                  }
                />
              </Form.Item>

              <Form.Item
                label="Contact Number"
                name="delivery_dealerContactNumber"
                className="mb-0"
              >
                <Input
                  className={inputClassName}
                  onChange={(e) =>
                    syncDealerFields({
                      delivery_dealerContactNumber: e.target.value || "",
                    })
                  }
                />
              </Form.Item>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label="Dealer Address"
                name="delivery_dealerAddress"
                className="mb-0"
              >
                <Input.TextArea
                  className={textAreaClassName}
                  autoSize={{ minRows: 2, maxRows: 5 }}
                  onChange={(e) =>
                    syncDealerFields({
                      delivery_dealerAddress: e.target.value || "",
                    })
                  }
                />
              </Form.Item>

              <Form.Item
                label="Delivery By"
                name="delivery_by"
                className="mb-0"
              >
                <Input className={inputClassName} placeholder="e.g., Showroom Staff" />
              </Form.Item>
            </div>
          </div>
        </DeliverySection>
      )}

      {/* Insurance Details Section */}
      {isNewCar && (
        <DeliverySection
          id="delivery-insurance"
          icon="Shield"
          iconTone="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200"
          title="Insurance Details"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
              <Form.Item
                label="Insurance By"
                name="insurance_by"
                className="mb-0"
              >
                <Select
                  className="h-10"
                  placeholder="Select"
                  options={[
                    { label: "Autocredits India LLP", value: "Autocredits India LLP" },
                    { label: "Customer", value: "Customer" },
                    { label: "Showroom", value: "Showroom" },
                    { label: "Broker", value: "Broker" },
                  ]}
                  value={insuranceBy || undefined}
                  onChange={(value) => form.setFieldValue("insurance_by", value || "")}
                  allowClear
                />
              </Form.Item>

              <Form.Item
                label="Insurance Company Name"
                name="insurance_company_name"
                className="mb-0"
              >
                <AutoComplete
                  className="w-full"
                  options={IRDAI_INSURANCE_COMPANIES.map((company) => ({
                    value: company,
                    label: company,
                  }))}
                  filterOption={(input, option) =>
                    String(option?.value || "")
                      .toLowerCase()
                      .includes(String(input || "").toLowerCase())
                  }
                >
                  <Input className={inputClassName} placeholder="Start typing insurer name" />
                </AutoComplete>
              </Form.Item>

              <Form.Item
                label="Policy Number"
                name="insurance_policy_number"
                className="mb-0"
              >
                <Input className={inputClassName} placeholder="e.g., POL123456" />
              </Form.Item>

              <Form.Item
                label="Insurance Premium"
                name="insurance_premium"
                className="mb-0"
              >
                <InputNumber
                  className="h-10 w-full"
                  controls={false}
                  placeholder="e.g., 60,935"
                  formatter={(value) => {
                    if (value === undefined || value === null || value === "") return "";
                    const cleaned = String(value).replace(/[^\d.-]/g, "");
                    if (!cleaned || cleaned === "-" || cleaned === ".") return "";
                    const parsed = Number(cleaned);
                    return Number.isFinite(parsed)
                      ? parsed.toLocaleString("en-IN")
                      : cleaned;
                  }}
                  parser={(value) => String(value || "").replace(/[^\d.-]/g, "")}
                />
              </Form.Item>

              <Form.Item
                label="Policy Start Date"
                name="insurance_policy_start_date"
                className="mb-0"
                getValueProps={(val) => ({ value: val })}
              >
                <DateInput />
              </Form.Item>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <Form.Item
                label="Policy Duration"
                name="insurance_policy_duration_od"
                className="mb-0"
              >
                <Select
                  className="h-10"
                  placeholder="Select duration"
                  options={POLICY_DURATION_OPTIONS}
                  allowClear
                />
              </Form.Item>

              <Form.Item
                label="Policy End Date (OD)"
                name="insurance_policy_end_date_od"
                className="mb-0"
                getValueProps={(val) => ({ value: val })}
              >
                <DateInput disabled />
              </Form.Item>
            </div>
          </div>
        </DeliverySection>
      )}

      {/* Invoice Details Section */}
      {isNewCar && (
        <DeliverySection
          id="delivery-invoice"
          icon="FileText"
          iconTone="bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-200"
          title="Invoice Details"
          aside={
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-700 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-200">
              RC/INV Storage Number: {rcInvStorageNumber || "-"}
            </div>
          }
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Form.Item
                label="Invoice Number"
                name="invoice_number"
                className="mb-0"
              >
                <Input className={inputClassName} placeholder="e.g., INV-2025-001" />
              </Form.Item>

              <Form.Item
                label="Invoice Date"
                name="invoice_date"
                className="mb-0"
                getValueProps={(val) => ({ value: val })}
              >
                <DateInput />
              </Form.Item>

              <Form.Item
                label="Received As"
                name="invoice_received_as"
                className="mb-0"
              >
                <Select
                  className="h-10"
                  placeholder="Select"
                  options={[
                    { label: "Original", value: "Original" },
                    { label: "Photocopy", value: "Photocopy" },
                  ]}
                  allowClear
                />
              </Form.Item>

              <Form.Item
                label="Received From"
                name="invoice_received_from"
                className="mb-0"
              >
                <Input className={inputClassName} placeholder="e.g., Dealer" />
              </Form.Item>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Form.Item
                label="Received Date"
                name="invoice_received_date"
                className="mb-0"
                getValueProps={(val) => ({ value: val })}
              >
                <DateInput />
              </Form.Item>

              <UploadField
                label="Upload Invoice"
                file={invoiceFile}
                uploading={uploadingInvoice}
                onOpenUpload={() => setShowInvoiceUploadModal(true)}
                onView={() => setViewInvoice(true)}
                onRemove={async () => {
                  setInvoiceFile(null);
                  const nextDocs = removeDeliveryDocument(
                    form.getFieldValue("postfile_documents") || [],
                    "INVOICE",
                  );
                  form.setFieldValue("postfile_documents", nextDocs);
                await persistDeliveryPatch({
                  delivery_invoiceFile: "",
                  postfile_documents: toPersistablePostFileDocuments(nextDocs),
                }, { silent: false });
              }}
            />
            </div>
          </div>
        </DeliverySection>
      )}

      {/* RC Details Section */}
      <DeliverySection
        id="delivery-rc"
        icon="CreditCard"
        iconTone="bg-violet-100 text-violet-700 dark:bg-violet-900/60 dark:text-violet-200"
        title="RC Details"
        aside={
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-700 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-200">
            RC/INV Storage Number: {rcInvStorageNumber || "-"}
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Form.Item label="Regd No" name="rc_redg_no" className="mb-0">
              <AutoComplete
                options={registrationLookupOptions}
                onSearch={searchRegistrationLookup}
                onSelect={handleRegistrationSelect}
                filterOption={false}
                allowClear
              >
                <Input
                  className={inputClassName}
                  placeholder="e.g., DL01AB1234 (or last 4 digits)"
                  suffix={
                    registrationLookupLoading ? (
                      <span className="text-[10px] text-muted-foreground animate-pulse">
                        Searching...
                      </span>
                    ) : null
                  }
                />
              </AutoComplete>
            </Form.Item>

            <Form.Item
              label="Year of Manufacture"
              name="yearOfManufacture"
              className="mb-0"
            >
              <Input maxLength={4} className={inputClassName} placeholder="e.g., 2026" />
            </Form.Item>

            <Form.Item label="Chassis No" name="rc_chassis_no" className="mb-0">
              <Input className={inputClassName} placeholder="e.g., MA3EXXXX" />
            </Form.Item>

            <Form.Item label="Engine No" name="rc_engine_no" className="mb-0">
              <Input className={inputClassName} placeholder="e.g., K15BXXXX" />
            </Form.Item>

            <Form.Item
              label="Regd Date"
              name="rc_redg_date"
              className="mb-0"
              getValueProps={(val) => ({ value: val })}
            >
              <DateInput />
            </Form.Item>

            <Form.Item
              label="Received As"
              name="rc_received_as"
              className="mb-0"
            >
              <Select
                className="h-10"
                placeholder="Select"
                options={[
                  { label: "Original", value: "Original" },
                  { label: "Photocopy", value: "Photocopy" },
                ]}
                allowClear
              />
            </Form.Item>

            <Form.Item
              label="Received From"
              name="rc_received_from"
              className="mb-0"
            >
              <Input className={inputClassName} placeholder="e.g., RTO Office" />
            </Form.Item>

            <Form.Item
              label="Received Date"
              name="rc_received_date"
              className="mb-0"
              getValueProps={(val) => ({ value: val })}
            >
              <DateInput />
            </Form.Item>
          </div>

          <div
            className={`grid grid-cols-1 md:grid-cols-2 ${showHypothecation ? "xl:grid-cols-4" : "xl:grid-cols-3"} gap-4`}
          >
            {showHypothecation && (
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">
                  Hypothecation
                </label>
                <Input
                  className="h-10"
                  value={approvalBankName || "-"}
                  readOnly
                  prefix={<Icon name="Building2" size={14} className="text-primary" />}
                />
              </div>
            )}

            <UploadField
              label="RC Front"
              file={rcFile}
              uploading={uploadingRc}
              onOpenUpload={() => setShowRcUploadModal(true)}
              onView={() => setViewRc(true)}
              onRemove={async () => {
                setRcFile(null);
                const nextDocs = removeDeliveryDocument(
                  form.getFieldValue("postfile_documents") || [],
                  "RC",
                );
                form.setFieldValue("postfile_documents", nextDocs);
                await persistDeliveryPatch({
                  delivery_rcFile: "",
                  postfile_documents: toPersistablePostFileDocuments(nextDocs),
                }, { silent: false });
              }}
            />

            <UploadField
              label="RC Back"
              file={rcBackFile}
              uploading={uploadingRcBack}
              onOpenUpload={() => setShowRcBackUploadModal(true)}
              onView={() => setViewRcBack(true)}
              onRemove={async () => {
                setRcBackFile(null);
                const nextDocs = removeDeliveryDocument(
                  form.getFieldValue("postfile_documents") || [],
                  "RC_BACK",
                );
                form.setFieldValue("postfile_documents", nextDocs);
                await persistDeliveryPatch({
                  delivery_rcBackFile: "",
                  postfile_documents: toPersistablePostFileDocuments(nextDocs),
                }, { silent: false });
              }}
            />
          </div>
        </div>
      </DeliverySection>

      {isCashCase && (
        <DeliverySection
          id="delivery-doc-management"
          icon="FolderOpen"
          iconTone="bg-rose-100 text-rose-700 dark:bg-rose-900/60 dark:text-rose-200"
          title="Document Management"
        >
          <PostFileDocumentManagement
            form={form}
            loanId={effectiveLoanRecordId || undefined}
            isEditMode={Boolean(effectiveLoanRecordId)}
          />
        </DeliverySection>
      )}

      <LoanDocumentUploadModal
        open={showInvoiceUploadModal}
        title="Upload Documents"
        onUpload={handleInvoiceUpload}
        onClose={() => setShowInvoiceUploadModal(false)}
        uploading={uploadingInvoice}
        progress={invoiceUploadProgress}
        accept="image/*,.pdf,.doc,.docx"
        multiple={false}
      />

      <LoanDocumentUploadModal
        open={showRcUploadModal}
        title="Upload Documents"
        onUpload={handleRcUpload}
        onClose={() => setShowRcUploadModal(false)}
        uploading={uploadingRc}
        progress={rcUploadProgress}
        accept="image/*,.pdf,.doc,.docx"
        multiple={false}
      />

      <LoanDocumentUploadModal
        open={showRcBackUploadModal}
        title="Upload Documents"
        onUpload={handleRcBackUpload}
        onClose={() => setShowRcBackUploadModal(false)}
        uploading={uploadingRcBack}
        progress={rcBackUploadProgress}
        accept="image/*,.pdf,.doc,.docx"
        multiple={false}
      />

      {/* Invoice Viewer Modal */}
      {viewInvoice && invoiceFile && (
        <LoanDocumentViewerModal
          open={viewInvoice}
          title="Post-File Document Viewer"
          documents={[{ ...invoiceFile, tag: "Invoice" }]}
          onClose={() => setViewInvoice(false)}
        />
      )}

      {/* RC Viewer Modal */}
      {viewRc && rcFile && (
        <LoanDocumentViewerModal
          open={viewRc}
          title="Post-File Document Viewer"
          documents={[{ ...rcFile, tag: "RC Front" }]}
          onClose={() => setViewRc(false)}
        />
      )}

      {/* RC Back Viewer Modal */}
      {viewRcBack && rcBackFile && (
        <LoanDocumentViewerModal
          open={viewRcBack}
          title="Post-File Document Viewer"
          documents={[{ ...rcBackFile, tag: "RC Back" }]}
          onClose={() => setViewRcBack(false)}
        />
      )}
    </div>
  );
};

export default VehicleDeliveryStep;
